import { CurrencyPipe, DatePipe } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { Title } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from "@ng-bootstrap/ng-bootstrap";
import {
  AgAnnualStatusComponent
} from "@shared/components/grid/ag-cells/annual-statements/ag-annual-status/ag-annual-status.component";
import {
  AgAsicDocumentStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-asic-document-status/ag-asic-document-status.component";
import {
  AgDocumentSigningStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-signing-status/ag-document-signing-status.component";
import {
  AgDocumentStatusComponent
} from "@shared/components/grid/ag-cells/common/ag-document-status/ag-document-status.component";
import {
  AgTagEntityTitleComponent
} from "@shared/components/grid/ag-cells/common/ag-tag-entity-title/ag-tag-entity-title.component";
import { ReplaceStrPipe } from "@shared/pipes/replace-str.pipe";
import { AnnualStatementsApiService } from "@shared/services/annual-statements-api.service";
import { AnnualStatementsService } from "@shared/services/annual-statements.service";
import { DocumentsApiService } from "@shared/services/documents-api.service";
import { DocumentCommonService } from "@shared/services/documents-common.service";
import { FilesApiService } from "@shared/services/files-api.service";
import { MenuService } from "@shared/services/menu.service";
import { downloadBase64File } from "@shared/utils/download-base64-file";
import { getPageTitle } from "@shared/utils/get-page-title";
import { Guid } from "@shared/utils/guid.helper";
import {
  getAnnualStatementsPath,
  getGenerateDocumentPath,
  getImportedAnnualStatementPagePath,
  getImportedDocumentPagePath
} from "@shared/utils/route-path-utils";
import { ToastrService } from "ngx-toastr";
import { catchError, of } from "rxjs";
import { AnnualStatement } from "@models/annual-statements/annual-statement";
import { Company } from "@models/companies/company";
import { Document } from "@models/documents/document";
import { DocumentSubFolderName } from "@models/common/document-sub-folder-name.enum";
import { EntityType } from "@models/common/entity-type.enum";
import { HttpErrorCode } from "@models/requests/http-error-code.enum";
import { FilesResponse } from "@models/ui-models/files";
import { DisclaimerComponent } from "@shared/components/commons/disclaimer/disclaimer.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { PageTitleComponent } from "@shared/components/page-title/page-title.component";
import { TagComponent } from "@shared/components/commons/tag/tag.component";
import { DocumentsListAccordionComponent } from "../documents-list-accordion/documents-list-accordion.component";

@Component({
  selector: 'app-imported-document-page',
  standalone: true,
  imports: [
    AgTagEntityTitleComponent,
    DatePipe,
    DocumentsListAccordionComponent,
    FormsModule,
    LoaderComponent,
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    PageTitleComponent,
    ReactiveFormsModule,
    TagComponent,
    AgDocumentStatusComponent,
    AgAsicDocumentStatusComponent,
    DisclaimerComponent,
    AgAnnualStatusComponent,
    AgDocumentSigningStatusComponent,
    CurrencyPipe,
    ReplaceStrPipe,
  ],
  providers: [],
  templateUrl: 'imported-document-page.component.html',
  styleUrl: 'imported-document-page.component.scss'
})
export class ImportedDocumentPageComponent implements OnInit {
  documentCommonService = inject(DocumentCommonService);
  documentsApiService = inject(DocumentsApiService);
  annualStatementsService = inject(AnnualStatementsService);
  annualStatementsApiService = inject(AnnualStatementsApiService);
  filesApiService = inject(FilesApiService);
  route: ActivatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  menuService = inject(MenuService);
  titleService = inject(Title);
  toastr = inject(ToastrService);
  destroyRef = inject(DestroyRef);

  readonly entityType = EntityType.Company;
  readonly DocumentSubFolderName = DocumentSubFolderName;

  pageHeader = '';
  modelId: string | null = null;
  document: Document | null = null;
  annualStatement: AnnualStatement | null = null;
  company: Company | null = null;
  files: FilesResponse | null = null;

  isSidebarCollapsed = signal(false);
  isLoading = signal(false);

  ngOnInit(): void {
    this.menuService.setMenuState(true);
    this.modelId = this.route.snapshot.paramMap.get('id') ?? null;
    this.tryLoadModel();
  }

  tryLoadModel(modelId = this.modelId): void {
    if (!modelId || !Guid.isValidGuid(modelId)) {
      void this.router.navigate(['/documents']);
      this.toastr.error('Link is not valid.');
      return;
    }

    if (this.router.url.includes(getImportedDocumentPagePath(modelId).join('/'))) {
      this.loadDocument();
    } else if (this.router.url.includes(getImportedAnnualStatementPagePath(modelId).join('/'))) {
      this.loadAnnualStatement();
    }
  }

  loadDocument(documentId = this.modelId!): void {
    this.pageHeader = ImportedDocumentPageComponent.importedDocumentTitle;
    this.titleService.setTitle(getPageTitle(ImportedDocumentPageComponent.importedDocumentTitle));
    this.isLoading.set(true);

    this.documentsApiService.getById(documentId)
      .subscribe({
        next: (document) => {
          if (!Document.isImportedDocument(document)) {
            this.router.navigate(getGenerateDocumentPath(documentId));
            return;
          }

          this.document = document;
          this.company = new Company(document.company);
          this.loadModelFiles();

          this.isLoading.set(false);
        },
        error: (error) => {
          console.warn('[loadDocument] error: ', error);

          if ((error.status as HttpErrorCode) === HttpErrorCode.NOT_FOUND) {
            this.toastr.error(`Document ${ documentId } not found`, 'Error!');
          } else {
            this.toastr.error(`${ error.message }`, 'Error!');
            console.error(error);
          }
        }
      });
  }

  loadAnnualStatement(annualStatementId = this.modelId): void {
    this.pageHeader = ImportedDocumentPageComponent.importedAnnualStatementTitle;
    this.titleService.setTitle(getPageTitle(ImportedDocumentPageComponent.importedAnnualStatementTitle));

    this.isLoading.set(true);

    this.annualStatementsApiService.getAnnualById(annualStatementId!)
      .subscribe({
        next: (annualStatement) => {
          if (!AnnualStatement.isImportedDocument(annualStatement)) {
            void this.router.navigate(getAnnualStatementsPath(annualStatementId!));
            return;
          }

          this.annualStatement = annualStatement;
          this.company = new Company(this.annualStatement.company);
          this.loadModelFiles();

          this.isLoading.set(false);
        }
      });
  }

  loadModelFiles(modelId = this.modelId!): void {
    this.filesApiService.getFiles(modelId)
      .subscribe({
        next: (files) => {
          this.files = files;
        },
        error: (error) => {
          console.warn('[loadDocumentFiles] error: ', error);
        }
      });
  }

  downloadCombinedPDF(subFolder: string): void {
  if (!this.modelId) {
    this.toastr.error('Error downloading file');
    return;
  }

  this.filesApiService
    .downloadFolder(this.modelId, true, subFolder)
    .pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.toastr.error("Download 'Generated documents.pdf' failed", "Error");
        return of('ERR');
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe((res) => {
      if (res !== 'ERR') {
        downloadBase64File(res, `Generated documents.pdf`);
      }
    });
}

  downloadZIP(subFolder: string): void {
  if (!this.modelId) {
    this.toastr.error('Error downloading file');
    return;
  }

  this.filesApiService
    .downloadFolder(this.modelId, false, subFolder)
    .pipe(
      catchError((error: HttpErrorResponse) => {
        console.error(error);
        this.toastr.error("Download 'Generated documents.zip' failed", "Error");
        return of('ERR');
      }),
      takeUntilDestroyed(this.destroyRef)
    )
    .subscribe((res) => {
      if (res !== 'ERR') {
        downloadBase64File(res, "Generated documents.zip");
      }
    });
}

  downloadFile(fileName: string, subFolder: string): void {
  if (!this.modelId) {
    this.toastr.error('Error downloading file');
    return;
  }
  this.documentCommonService.downloadFile(this.modelId, fileName, subFolder);
}

  static readonly importedDocumentTitle = 'Imported Document';
  static readonly importedAnnualStatementTitle = 'Imported Annual Statement';
}
